/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V3 - Cross-Page Navigation Fix) ---
 * - MISSION: To fix the critical bug where the "Get Pass" button in the
 * header only worked when clicked from the homepage.
 *
 * - THE BUG: The `MapsToIndikinPass` function only set a `pendingNavigation`
 * state. If the user was on a page where the target section (`IndikinPass`)
 * was not rendered (e.g., /login), the state would be set, but nothing
 * would happen until the user navigated back to the homepage.
 *
 * - THE FIX (Context-Aware Navigation):
 * 1.  NEW HOOKS: The provider now uses `useLocation` and `useNavigate` from
 * `react-router-dom` to be aware of the current page and to trigger navigation.
 * 2.  CONDITIONAL LOGIC: The `MapsToIndikinPass` function has been
 * completely rewritten.
 * 3.  If the user is already on the homepage (`/` or `/home2`), it sets
 * the `pendingNavigation` state directly, as before.
 * 4.  If the user is on *any other page*, it now uses `Maps` to first
 * go to the homepage (`/`) and passes the scroll request in the route's
 * state (`{ state: { pendingNav: ... } }`).
 *
 * - RESULT: This is the definitive fix. The "Get Pass" button is now
 * reliable across the entire site. It correctly navigates the user to the
 * homepage first if necessary, ensuring the target section is always
 * available to be scrolled to. This logic is handled in conjunction with
 * updates to `Home.jsx` to create a seamless user experience.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCarousel } from './CarouselContext';

const NavContext = createContext();

const homePaths = ['/', '/home2'];

// eslint-disable-next-line react-refresh/only-export-components
export const useNavContext = () => {
  const context = useContext(NavContext);
  if (!context) {
    throw new Error('useNavContext must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileSearchVisible, setMobileSearchVisible] = useState(false);
  const [isActivelySearching, setIsActivelySearching] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [dashboardSidebarState, setDashboardSidebarState] = useState('closed');
  const [targetToken, setTargetToken] = useState(null); // <-- NEW: For Ecosystem deep linking
  const [cookieBannerHeight, setCookieBannerHeight] = useState(0); // <-- NEW: Global layout metric


  const { setTargetSlide } = useCarousel();
  const { t, i18n } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    const filmmakersData = t('foundingFilmmakers.filmmakers', { returnObjects: true }) || [];
    const filmsData = t('featuredFilms.films', { returnObjects: true }) || [];
    const lowercasedQuery = searchQuery.toLowerCase();
    const filteredFilmmakers = filmmakersData.filter((filmmaker) =>
      filmmaker.name.toLowerCase().includes(lowercasedQuery)
    );
    const filteredFilms = filmsData.filter((film) =>
      film.title.toLowerCase().includes(lowercasedQuery)
    );
    setSearchResults({ filmmakers: filteredFilmmakers, films: filteredFilms });
  }, [searchQuery, t, i18n.language]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults(null);
    setIsActivelySearching(false);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleSearchFocus = useCallback(() => {
    setIsActivelySearching(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    setIsActivelySearching(false);
  }, []);

  const toggleMobileSearch = useCallback(() => {
    setMobileSearchVisible(prev => !prev);
  }, []);

  const handleDashboardSidebarToggle = useCallback((activeView) => {
    setDashboardSidebarState(prevState => {
      if (activeView === 'viewer') {
        return prevState === 'open' ? 'closed' : 'open';
      }
      return prevState === 'open' ? 'collapsed' : 'open';
    });
  }, []);

  const scrollToSection = useCallback((sectionId, customOptions = {}) => {
    const navPayload = {
      targetId: sectionId,
      scrollOptions: { behavior: 'smooth', block: 'start', ...customOptions },
    };

    if (homePaths.includes(location.pathname)) {
      setPendingNavigation(navPayload);
    } else {
      navigate('/', { state: { pendingNav: navPayload } });
    }
  }, [location.pathname, navigate, setPendingNavigation]);

  const navigateTo = useCallback((item) => {
    clearSearch();
    const targetId = item.type === 'film' ? 'featured-films-carousel' : 'filmmakers-carousel';
    if (homePaths.includes(location.pathname)) {
      setPendingNavigation({ targetId, slide: item });
    } else {
      navigate('/', { state: { pendingNav: { targetId, slide: item } } });
    }
  }, [clearSearch, location.pathname, navigate]);

  const clearPendingNavigation = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  const navigateToIndikinPass = useCallback(() => {
    const navPayload = {
      targetId: 'indikin-pass',
      // HIDDEN: Carousel removed — no slide targeting needed
      // slide: 1, // Stage 2 (Stage 1 is sold out)
    };
    if (homePaths.includes(location.pathname)) {
      setPendingNavigation(navPayload);
    } else {
      navigate('/', { state: { pendingNav: navPayload } });
    }
  }, [location.pathname, navigate]);

  const value = {
    searchQuery,
    searchResults,
    isMenuOpen,
    setIsMenuOpen,
    handleSearchChange,
    navigateTo,
    navigateToIndikinPass,
    clearSearch,
    setTargetSlide,
    scrollToSection,
    isActivelySearching,
    handleSearchFocus,
    handleSearchBlur,
    pendingNavigation,
    setPendingNavigation, // <-- Expose setter for Home.jsx
    clearPendingNavigation,
    isMobileSearchVisible,
    toggleMobileSearch,
    dashboardSidebarState,
    setDashboardSidebarState,
    handleDashboardSidebarToggle,
    targetToken,
    setTargetToken, // <-- Expose for Ecosystem.jsx
    cookieBannerHeight,
    setCookieBannerHeight,
  };


  return (
    <NavContext.Provider value={value}>
      {children}
    </NavContext.Provider>
  );
};

